import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { Search, User, LogOut, Languages, Settings, Users } from 'lucide-react';
import { LanguageSwitcher } from './LanguageSwitcher';
import { useTranslation } from 'react-i18next';

export const SettingsDropdown = () => {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const { t } = useTranslation();
  const [username, setUsername] = useState<string>('');
  const [userType, setUserType] = useState<string>('');

  useEffect(() => {
    if (user) {
      loadUserProfile();
    }
  }, [user]);

  const loadUserProfile = async () => {
    try {
      // Get user profile to check user type
      const { data: userProfile } = await supabase
        .from('user_profiles')
        .select('user_type')
        .eq('user_id', user!.id)
        .single();

      if (userProfile?.user_type) {
        setUserType(userProfile.user_type);
      }

      // Get user's first organization's username
      const { data: orgMember } = await supabase
        .from('organization_members')
        .select('org_id, organizations!fk_organization_members_organization(username)')
        .eq('user_id', user!.id)
        .not('organizations.username', 'is', null)
        .limit(1)
        .single();

      if (orgMember?.organizations?.username) {
        setUsername(orgMember.organizations.username);
      }
    } catch (error) {
      console.error('Error loading user profile:', error);
    }
  };

  const handleViewProfile = () => {
    if (username) {
      navigate(`/${username}`);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" className="w-full sm:w-auto h-10 px-4 gap-2">
          <Settings className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-56 bg-background border shadow-lg z-50" align="end" forceMount>
        {user ? (
          <>
            <div className="flex items-center justify-start gap-2 p-2">
              <div className="flex flex-col space-y-1 leading-none">
                {username && (
                  <p className="text-sm font-medium leading-none">@{username}</p>
                )}
                <p className="text-xs leading-none text-muted-foreground">
                  {user?.email}
                </p>
              </div>
            </div>
            <DropdownMenuSeparator />
            {username && !['carrier_company', 'broker', 'driver'].includes(userType) && (
              <DropdownMenuItem onClick={handleViewProfile}>
                <User className="ltr:mr-2 rtl:ml-2 h-4 w-4" />
                {t('settings.viewProfile')}
              </DropdownMenuItem>
            )}
            {!['carrier_company', 'broker', 'driver'].includes(userType) && (
              <DropdownMenuItem onClick={() => navigate('/carriers')}>
                <Users className="ltr:mr-2 rtl:ml-2 h-4 w-4" />
                {t('settings.manageCarriers')}
              </DropdownMenuItem>
            )}
            <DropdownMenuSeparator />
            <div className="px-2 py-1.5">
              <LanguageSwitcher />
            </div>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={async () => {
              await signOut();
              navigate('/auth');
            }}>
              <LogOut className="ltr:mr-2 rtl:ml-2 h-4 w-4" />
              {t('settings.signOut')}
            </DropdownMenuItem>
          </>
        ) : (
          <>
            <div className="flex items-center justify-start gap-2 p-2">
              <div className="flex flex-col space-y-1 leading-none">
                <p className="text-sm font-medium leading-none">{t('settings.language')}</p>
                <p className="text-xs leading-none text-muted-foreground">
                  {t('settings.changeLanguage')}
                </p>
              </div>
            </div>
            <DropdownMenuSeparator />
            <div className="px-2 py-1.5">
              <LanguageSwitcher />
            </div>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};